import * as React from 'react';

import { observer } from 'mobx-react';
import moment from 'moment';

import { EXPORT_TYPES, exportData, getErrorMessage, IHeaders } from 'common-utils';
import { showConfirmDialogWithoutCancel } from 'ui-lib';
import { ConstrainedDateRangePicker } from 'pages/private/components/constrained-date-range-picker';
import { DATE_TIME_FORMAT, DEFAULT_HISTORICAL_RANGE, formatDate } from 'common-utils/date-utils';

import { DeviceLogsTable, IDeviceLogsProps, useDeviceLogsStore } from './device-log-table';
import { TableUtils } from 'pages/private/components/table-utils';

const EXPORT_HEADERS: IHeaders = {
    timestamp: 'Timestamp',
    data: 'Device Logs',
};

export const RaptorDeviceLogsComponent = observer((props: IDeviceLogsProps) => {
    const store = useDeviceLogsStore(props);

    const downloadLogFile = () => {
        try {
            const logType = store.defaultLogType;
            const deviceLogs = store.displayDeviceLogs(logType);
            if (deviceLogs.length === 0) {
                showConfirmDialogWithoutCancel({
                    title: 'No logs found',
                    content: 'No logs data found for the provided type',
                    okText: 'OK',
                });
                return;
            }
            exportData(
                `deviceLogs_${logType.toLowerCase()}_${formatDate(store.dateFrom.toDate(), DATE_TIME_FORMAT.DEFAULT)}_${formatDate(store.dateTo.toDate(), DATE_TIME_FORMAT.DEFAULT)}.csv`,
                EXPORT_TYPES.CSV,
                deviceLogs,
                EXPORT_HEADERS,
            );
        } catch (err) {
            showConfirmDialogWithoutCancel({
                title: 'Error',
                content: getErrorMessage(err),
                okText: 'OK',
            });
        }
    };

    return (
        <DeviceLogsTable
            store={store}
            columns={[
                TableUtils.createBasicFilterTimeColumn({
                    key: 'timestamp',
                    title: 'Timestamp',
                    defaultSortOrder: 'ascend',
                    columnExtraOptions: {
                        filterDropdown: undefined,
                        onFilter: undefined,
                        width: '200px',
                    },
                }),
                TableUtils.createBasicFilterColumn({
                    key: 'data',
                    title: 'Device Logs',
                }),
            ]}
            isProcessing={false}
            onDownload={downloadLogFile}
            datePicker={
                <ConstrainedDateRangePicker
                    key='date-range-picker'
                    range={DEFAULT_HISTORICAL_RANGE}
                    dateFormat={DATE_TIME_FORMAT.DATE_AND_SHORT_TIME}
                    showTime={true}
                    setFilterByDateFrom={(dateFrom: Date) => store.setDateFrom(moment(dateFrom))}
                    setFilterByDateTo={(dateTo: Date) => store.setDateTo(moment(dateTo))}
                    filterByDateFrom={store.dateFrom.toDate()}
                    filterByDateTo={store.dateTo.toDate()}
                    loading={store.loading}
                    onLoad={() => store.initialize(true)}
                />
            }
        />
    );
});
